import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertTriangle, Building2, Check, ChevronLeft, ChevronRight, Edit3, Filter,
  Globe2, Mail, Plus, RefreshCw, Search, Shield, Trash2, User, Users, X
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { apiClient } from '../config/api'
import { useAuth } from '../contexts/AuthContext'
import type { AccountType, DataScope, UserRole } from '../services/authService'

type UserState = 'PENDING' | 'ACTIVE' | 'SUSPENDED' | 'REVOKED'
type View = 'CUSTOMER' | 'FLEET_TEAM' | 'INVITATIONS'

interface RoleTemplate {
  id: number
  name: string
  displayName: string
  description?: string | null
  family: AccountType
  isSystem: boolean
  isActive: boolean
  capabilities: string[]
}

interface Membership {
  membershipId?: number | null
  customerId: number
  customerName: string
  role: UserRole
  roleTemplateId?: number | null
  roleTemplate?: RoleTemplate | null
  capabilities: string[]
  platforms: string[]
  status: string
  isPrimary?: boolean
}

interface SystemUser {
  id: number
  username: string
  email: string
  fullName: string
  role: UserRole
  state: UserState
  accountType: AccountType
  dataScope: DataScope
  roleTemplateId?: number | null
  roleTemplate?: RoleTemplate | null
  isPlatformAdmin: boolean
  memberships: Membership[]
  workspaces: Membership[]
  capabilities: string[]
  platforms: string[]
}

interface CustomerOption {
  id: number
  companyName: string
  customerCode?: string
}

interface CapabilityOption {
  key: string
  description?: string
  category: string
  accountTypes: AccountType[]
  platforms: string[]
}

interface PlatformOption {
  key: string
  name: string
}

interface CustomerPlatformRequirement {
  customerId: number
  platformKey: string
}

interface UserDraft {
  username: string
  email: string
  fullName: string
  accountType: AccountType
  dataScope: DataScope
  customerIds: number[]
  roleByCustomer: Record<string, number>
  fleetRoleTemplateId: number | null
  platforms: string[]
  capabilitiesByScope: Record<string, string[]>
  advanced: boolean
  capabilityScope: string
  state: UserState
}

interface UserManagementProps {
  onUserSelect?: (user: SystemUser) => void
  onUsersChange?: (users: SystemUser[]) => void
  showMessage?: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void
  showConfirm?: (message: string) => Promise<boolean>
}

const FLEET_PLATFORM = 'FLEET_PORTAL'
const FLEET_SCOPE = 'fleet'
const dangerousCapabilities = new Set(['users.manage', 'settings.manage', 'mail.manage', 'customers.manage'])

const normalizeRoleTemplate = (row: any): RoleTemplate | null => row ? ({
  id: Number(row.id),
  name: String(row.name || ''),
  displayName: String(row.displayName ?? row.display_name ?? row.name ?? ''),
  description: row.description,
  family: String(row.family || 'CUSTOMER').toUpperCase() as AccountType,
  isSystem: Boolean(row.isSystem ?? row.is_system),
  isActive: Boolean(row.isActive ?? row.is_active ?? true),
  capabilities: Array.isArray(row.capabilities) ? row.capabilities.map(String) : []
}) : null

const normalizeMembership = (row: any, fallbackRole: UserRole): Membership => ({
  membershipId: row.membershipId == null && row.membership_id == null ? null : Number(row.membershipId ?? row.membership_id),
  customerId: Number(row.customerId ?? row.customer_id),
  customerName: String(row.customerName ?? row.customer_name ?? ''),
  role: String(row.role || fallbackRole || 'VIEWER').toUpperCase() as UserRole,
  roleTemplateId: row.roleTemplateId == null && row.role_template_id == null
    ? null : Number(row.roleTemplateId ?? row.role_template_id),
  roleTemplate: normalizeRoleTemplate(row.roleTemplate ?? row.role_template),
  capabilities: Array.isArray(row.capabilities) ? row.capabilities.map(String) : [],
  platforms: Array.isArray(row.platforms) ? row.platforms.map(String) : [],
  status: String(row.status || 'ACTIVE').toUpperCase(),
  isPrimary: Boolean(row.isPrimary ?? row.is_primary)
})

const normalizeUser = (row: any): SystemUser => {
  const role = String(row.role || 'VIEWER').toUpperCase() as UserRole
  const accountType = String(row.accountType ?? row.account_type ?? 'CUSTOMER').toUpperCase() as AccountType
  const memberships = Array.isArray(row.memberships)
    ? row.memberships.map((item: any) => normalizeMembership(item, role))
    : row.customerId != null || row.customer_id != null
      ? [normalizeMembership({
        customerId: row.customerId ?? row.customer_id,
        customerName: row.customerName ?? row.customer_name,
        role,
        platforms: row.platforms
      }, role)] : []
  const workspaceRows = Array.isArray(row.workspaces) ? row.workspaces : memberships
  const workspaces = workspaceRows.map((item: any) => normalizeMembership(item, role))
  return {
    id: Number(row.id),
    username: String(row.username || ''),
    email: String(row.email || ''),
    fullName: String(row.fullName ?? row.full_name ?? ''),
    role,
    state: String(row.state ?? row.status ?? (row.isActive ?? row.is_active ? 'ACTIVE' : 'SUSPENDED')).toUpperCase() as UserState,
    accountType,
    dataScope: String(row.dataScope ?? row.data_scope ?? 'SELECTED_CUSTOMERS').toUpperCase() as DataScope,
    roleTemplateId: row.roleTemplateId == null && row.role_template_id == null
      ? null : Number(row.roleTemplateId ?? row.role_template_id),
    roleTemplate: normalizeRoleTemplate(row.roleTemplate ?? row.role_template),
    isPlatformAdmin: Boolean(row.isPlatformAdmin ?? row.is_platform_admin),
    memberships,
    workspaces,
    capabilities: Array.isArray(row.capabilities) ? row.capabilities.map(String) : (memberships[0]?.capabilities || []),
    platforms: Array.isArray(row.platforms)
      ? row.platforms.map(String)
      : [...new Set(memberships.flatMap(item => item.platforms))]
  }
}

const blankDraft = (accountType: AccountType, customerIds: number[] = []): UserDraft => ({
  username: '',
  email: '',
  fullName: '',
  accountType,
  dataScope: 'SELECTED_CUSTOMERS',
  customerIds,
  roleByCustomer: {},
  fleetRoleTemplateId: null,
  platforms: [FLEET_PLATFORM],
  capabilitiesByScope: {},
  advanced: false,
  capabilityScope: accountType === 'FLEET_TEAM' ? FLEET_SCOPE : String(customerIds[0] || ''),
  state: 'ACTIVE'
})

const sameSet = (left: string[], right: string[]) =>
  left.length === right.length && left.every(item => right.includes(item))

const UserManagement: React.FC<UserManagementProps> = ({
  onUserSelect, onUsersChange, showMessage, showConfirm
}) => {
  const { t } = useTranslation()
  const { activeCustomerId, isPlatformAdmin, workspaces, can } = useAuth()
  const containerRef = useRef<HTMLDivElement>(null)
  const [users, setUsers] = useState<SystemUser[]>([])
  const [customers, setCustomers] = useState<CustomerOption[]>([])
  const [roles, setRoles] = useState<RoleTemplate[]>([])
  const [capabilities, setCapabilities] = useState<CapabilityOption[]>([])
  const [platforms, setPlatforms] = useState<PlatformOption[]>([])
  const [customerPlatformRequirements, setCustomerPlatformRequirements] = useState<CustomerPlatformRequirement[]>([])
  const [view, setView] = useState<View>('CUSTOMER')
  const [search, setSearch] = useState('')
  const [stateFilter, setStateFilter] = useState('current')
  const [scopeFilter, setScopeFilter] = useState('all')
  const [showFilters, setShowFilters] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [mode, setMode] = useState<'create' | 'edit' | null>(null)
  const [selected, setSelected] = useState<SystemUser | null>(null)
  const [draft, setDraft] = useState<UserDraft>(() => blankDraft('CUSTOMER'))
  const [step, setStep] = useState(1)

  const customerRoles = roles.filter(role =>
    role.family === 'CUSTOMER' && role.isActive &&
    (isPlatformAdmin || role.capabilities.every(capability => can(capability)))
  )
  const fleetRoles = roles.filter(role => role.family === 'FLEET_TEAM' && role.isActive)

  const loadUsers = async () => {
    setLoading(true)
    try {
      const response = await apiClient.get('/users')
      const rows = Array.isArray(response.data) ? response.data : response.data?.users || []
      const normalized = rows.map(normalizeUser)
      setUsers(normalized)
      onUsersChange?.(normalized)
    } catch (error: any) {
      showMessage?.(error?.message || t('users_load_failed'), 'error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadUsers()
    apiClient.get('/access/catalog').then(response => {
      const data = response.data || {}
      setRoles((data.roleTemplates || [])
        .map((role: any) => normalizeRoleTemplate(role))
        .filter(Boolean) as RoleTemplate[])
      setCapabilities((data.capabilities || []).map((item: any) => ({
        key: String(item.key ?? item.capability_key ?? item),
        description: item.description,
        category: String(item.category || 'General'),
        accountTypes: (item.accountTypes ?? item.account_types ?? []).map(
          (accountType: unknown) => String(accountType).toUpperCase() as AccountType
        ),
        platforms: (item.platforms || []).map(String)
      })))
      setPlatforms((data.platforms || []).map((item: any) => ({
        key: String(item.key ?? item.platform_key),
        name: String(item.name ?? item.display_name ?? item.key ?? item.platform_key)
      })))
      setCustomerPlatformRequirements((data.customerPlatformRequirements || []).map((item: any) => ({
        customerId: Number(item.customerId ?? item.customer_id),
        platformKey: String(item.platformKey ?? item.platform_key)
      })))
    }).catch((error: any) => showMessage?.(error?.message || t('capabilities_load_failed'), 'warning'))

    if (isPlatformAdmin) {
      apiClient.get('/customers').then(response => {
        const rows = Array.isArray(response.data) ? response.data : response.data?.customers || []
        setCustomers(rows.map((row: any) => ({
          id: Number(row.id),
          companyName: String(row.companyName ?? row.company_name ?? row.name ?? row.id),
          customerCode: row.customerCode ?? row.customer_code
        })))
      }).catch(() => setCustomers([]))
    } else {
      setCustomers(workspaces.filter(item => item.customerId === activeCustomerId).map(item => ({
        id: item.customerId,
        companyName: item.customerName
      })))
    }
  }, [activeCustomerId, isPlatformAdmin])

  const groupedCapabilities = useMemo(() => capabilities
    .filter(capability =>
      (!draft || capability.accountTypes.includes(draft.accountType)) &&
      capability.platforms.some(platform => draft.platforms.includes(platform)) &&
      can(capability.key)
    )
    .reduce<Record<string, CapabilityOption[]>>((groups, capability) => {
      ;(groups[capability.category] ||= []).push(capability)
      return groups
    }, {}), [capabilities, can, draft.accountType, draft.platforms])

  const filteredUsers = users.filter(user => {
    const inView = view === 'INVITATIONS'
      ? user.state === 'PENDING'
      : user.state !== 'PENDING' && user.accountType === view
    const haystack = `${user.username} ${user.fullName} ${user.email} ${user.workspaces.map(item => item.customerName).join(' ')}`.toLowerCase()
    return inView && (!search || haystack.includes(search.toLowerCase()))
      && (stateFilter === 'all' || (stateFilter === 'current' ? user.state !== 'REVOKED' : user.state === stateFilter))
      && (scopeFilter === 'all' || user.dataScope === scopeFilter)
  })

  const requiredPlatformsFor = (accountType: AccountType, customerIds: number[]): string[] => {
    if (accountType === 'FLEET_TEAM') return [FLEET_PLATFORM]
    const required = new Set<string>()
    customerIds.forEach(customerId => {
      const mapped = customerPlatformRequirements
        .filter(item => item.customerId === customerId)
        .map(item => item.platformKey)
      if (mapped.length) mapped.forEach(platform => required.add(platform))
      else required.add(FLEET_PLATFORM)
    })
    return [...required]
  }

  const setAccountType = (accountType: AccountType) => {
    if (!isPlatformAdmin && accountType === 'FLEET_TEAM') return
    const lockedCustomers = isPlatformAdmin ? [] : activeCustomerId == null ? [] : [activeCustomerId]
    const customerIds = accountType === 'CUSTOMER' ? lockedCustomers : []
    setDraft(current => ({
      ...blankDraft(accountType, customerIds),
      username: current.username,
      email: current.email,
      fullName: current.fullName,
      state: current.state,
      platforms: requiredPlatformsFor(accountType, customerIds)
    }))
  }

  const startCreate = () => {
    const accountType: AccountType = view === 'FLEET_TEAM' && isPlatformAdmin ? 'FLEET_TEAM' : 'CUSTOMER'
    const lockedCustomers = !isPlatformAdmin && activeCustomerId != null ? [activeCustomerId] : []
    const customerIds = accountType === 'CUSTOMER' ? lockedCustomers : []
    setDraft({
      ...blankDraft(accountType, customerIds),
      platforms: requiredPlatformsFor(accountType, customerIds)
    })
    setSelected(null)
    setStep(1)
    setMode('create')
  }

  const startEdit = (user: SystemUser) => {
    const roleByCustomer: Record<string, number> = {}
    const capabilitiesByScope: Record<string, string[]> = {}
    user.memberships.forEach(membership => {
      if (membership.roleTemplateId) roleByCustomer[String(membership.customerId)] = membership.roleTemplateId
      capabilitiesByScope[String(membership.customerId)] = membership.capabilities
    })
    if (user.accountType === 'FLEET_TEAM') capabilitiesByScope[FLEET_SCOPE] = user.capabilities
    const customerIds = (user.accountType === 'CUSTOMER' ? user.memberships : user.workspaces).map(item => item.customerId)
    const baseDifferences = user.accountType === 'FLEET_TEAM'
      ? !sameSet(user.capabilities, user.roleTemplate?.capabilities || roles.find(role => role.id === user.roleTemplateId)?.capabilities || [])
      : user.memberships.some(membership => !sameSet(
        membership.capabilities,
        roles.find(role => role.id === membership.roleTemplateId)?.capabilities || []
      ))
    setDraft({
      username: user.username,
      email: user.email,
      fullName: user.fullName,
      accountType: user.accountType,
      dataScope: user.dataScope,
      customerIds,
      roleByCustomer,
      fleetRoleTemplateId: user.roleTemplateId || null,
      platforms: user.platforms.length ? user.platforms : [FLEET_PLATFORM],
      capabilitiesByScope,
      advanced: baseDifferences,
      capabilityScope: user.accountType === 'FLEET_TEAM' ? FLEET_SCOPE : String(customerIds[0] || ''),
      state: user.state
    })
    setSelected(user)
    setStep(1)
    setMode('edit')
    onUserSelect?.(user)
  }

  const closeWizard = () => {
    setMode(null)
    setSelected(null)
    setStep(1)
  }

  const toggleCustomer = (customerId: number) => {
    if (!isPlatformAdmin) return
    setDraft(current => {
      const selectedNow = current.customerIds.includes(customerId)
      const customerIds = selectedNow
        ? current.customerIds.filter(id => id !== customerId)
        : [...current.customerIds, customerId]
      const roleByCustomer = { ...current.roleByCustomer }
      const capabilitiesByScope = { ...current.capabilitiesByScope }
      if (selectedNow) {
        delete roleByCustomer[String(customerId)]
        delete capabilitiesByScope[String(customerId)]
      }
      return {
        ...current,
        customerIds,
        roleByCustomer,
        capabilitiesByScope,
        platforms: requiredPlatformsFor(current.accountType, customerIds),
        capabilityScope: current.capabilityScope === String(customerId) ? String(customerIds[0] || '') : current.capabilityScope
      }
    })
  }

  const chooseRole = (scope: string, roleId: number) => {
    const role = roles.find(item => item.id === roleId)
    if (!role) return
    setDraft(current => ({
      ...current,
      fleetRoleTemplateId: scope === FLEET_SCOPE ? roleId : current.fleetRoleTemplateId,
      roleByCustomer: scope === FLEET_SCOPE ? current.roleByCustomer : { ...current.roleByCustomer, [scope]: roleId },
      capabilitiesByScope: {
        ...current.capabilitiesByScope,
        [scope]: role.capabilities.filter(key => {
          const capability = capabilities.find(item => item.key === key)
          return capability?.platforms.some(platform => current.platforms.includes(platform))
        })
      }
    }))
  }

  const baseCapabilities = (scope: string): string[] => {
    const roleId = scope === FLEET_SCOPE ? draft.fleetRoleTemplateId : draft.roleByCustomer[scope]
    return (roles.find(role => role.id === roleId)?.capabilities || []).filter(key => {
      const capability = capabilities.find(item => item.key === key)
      return capability?.platforms.some(platform => draft.platforms.includes(platform))
    })
  }

  const effectiveCapabilities = (scope: string): string[] => {
    const selected = draft.advanced
      ? (draft.capabilitiesByScope[scope] || baseCapabilities(scope))
      : baseCapabilities(scope)
    return selected.filter(key => {
      const capability = capabilities.find(item => item.key === key)
      return capability?.platforms.some(platform => draft.platforms.includes(platform))
    })
  }

  const capabilityOverrides = (scope: string) => {
    if (!draft.advanced) return []
    const base = new Set(baseCapabilities(scope))
    const effective = new Set(effectiveCapabilities(scope))
    return capabilities
      .filter(capability => base.has(capability.key) !== effective.has(capability.key))
      .map(capability => ({
        capabilityKey: capability.key,
        allowed: effective.has(capability.key)
      }))
  }

  const toggleCapability = (key: string) => {
    const scope = draft.capabilityScope
    const current = effectiveCapabilities(scope)
    setDraft(value => ({
      ...value,
      capabilitiesByScope: {
        ...value.capabilitiesByScope,
        [scope]: current.includes(key) ? current.filter(item => item !== key) : [...current, key]
      }
    }))
  }

  const validateStep = (): boolean => {
    if (step === 2 && (!draft.username.trim() || !draft.fullName.trim() || !draft.email.includes('@'))) {
      showMessage?.(t('user_required_fields'), 'warning')
      return false
    }
    if (step === 3) {
      const needsCustomers = draft.accountType === 'CUSTOMER' || draft.dataScope === 'SELECTED_CUSTOMERS'
      if (needsCustomers && !draft.customerIds.length) {
        showMessage?.(t('workspace_required'), 'warning')
        return false
      }
    }
    if (step === 4) {
      const valid = selected?.isPlatformAdmin || (draft.accountType === 'FLEET_TEAM'
        ? Boolean(draft.fleetRoleTemplateId)
        : draft.customerIds.every(id => Boolean(draft.roleByCustomer[String(id)])))
      if (!valid) {
        showMessage?.(t('access.wizard.roleRequired'), 'warning')
        return false
      }
    }
    return true
  }

  const next = () => {
    if (validateStep()) setStep(current => Math.min(7, current + 1))
  }

  const save = async () => {
    if (!draft.username.trim() || !draft.fullName.trim() || !draft.email.includes('@')) {
      return showMessage?.(t('user_required_fields'), 'warning')
    }
    const primaryCustomer = draft.customerIds[0]
    const primaryScope = draft.accountType === 'FLEET_TEAM' ? FLEET_SCOPE : String(primaryCustomer || '')
    const roleTemplateId = draft.accountType === 'FLEET_TEAM'
      ? draft.fleetRoleTemplateId
      : draft.roleByCustomer[primaryScope]
    if (!roleTemplateId && !selected?.isPlatformAdmin) {
      return showMessage?.(t('access.wizard.roleRequired'), 'warning')
    }
    const requiredPlatforms = requiredPlatformsFor(draft.accountType, draft.customerIds)
    const selectedPlatforms = [...new Set([...requiredPlatforms, ...draft.platforms])]
    setSaving(true)
    try {
      const payload: Record<string, unknown> = {
        accountType: draft.accountType,
        dataScope: draft.accountType === 'CUSTOMER' ? 'SELECTED_CUSTOMERS' : draft.dataScope,
      }
      if (isPlatformAdmin || mode === 'create') {
        payload.platforms = selectedPlatforms
      }
      if (!selected?.isPlatformAdmin) {
        payload.roleTemplateId = roleTemplateId
        payload.capabilityMode = draft.advanced ? 'OVERRIDE' : 'INHERIT'
        payload.capabilityOverrides = capabilityOverrides(primaryScope)
        if (draft.accountType === 'CUSTOMER') {
          payload.memberships = draft.customerIds.map(customerId => {
            const scope = String(customerId)
            return {
              customerId,
              roleTemplateId: draft.roleByCustomer[scope],
              capabilityMode: draft.advanced ? 'OVERRIDE' : 'INHERIT',
              capabilityOverrides: capabilityOverrides(scope)
            }
          })
        }
      }
      if (isPlatformAdmin || mode === 'create') {
        Object.assign(payload, {
          username: draft.username.trim(),
          email: draft.email.trim(),
          fullName: draft.fullName.trim()
        })
      }
      if (isPlatformAdmin || mode === 'create') payload.customerIds = draft.dataScope === 'GLOBAL' ? [] : draft.customerIds
      if (mode === 'edit' && !['PENDING', 'REVOKED'].includes(draft.state)) payload.isActive = draft.state === 'ACTIVE'

      if (mode === 'create') {
        const response = await apiClient.post('/users/invite', payload)
        if (response.data?.emailSent === false) {
          showMessage?.(response.data?.message || t('invitation_email_failed'), 'warning')
        } else {
          showMessage?.(t('invitation_sent'), 'success')
        }
      } else if (selected) {
        await apiClient.put(`/users/${selected.id}`, payload)
      }
      if (mode === 'edit') showMessage?.(t('user_updated', { name: draft.username }), 'success')
      closeWizard()
      await loadUsers()
    } catch (error: any) {
      showMessage?.(error?.message || t('user_save_failed'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const revokeAccess = async (user: SystemUser) => {
    if (showConfirm && !await showConfirm(t('access.users.revokeConfirm', { username: user.username }))) return
    try {
      await apiClient.delete(`/users/${user.id}`)
      showMessage?.(t('access.users.revoked'), 'success')
      await loadUsers()
    } catch (error: any) {
      showMessage?.(error?.message || t('user_save_failed'), 'error')
    }
  }

  const invitationAction = async (user: SystemUser, action: 'resend' | 'revoke') => {
    if (action === 'revoke' && showConfirm && !await showConfirm(t('access.invitations.revokeConfirm', { username: user.username }))) return
    try {
      await apiClient.post(`/users/${user.id}/invitations/${action}`)
      showMessage?.(t(action === 'resend' ? 'invitation_sent' : 'access.invitations.revoked'), 'success')
      if (action === 'revoke') await loadUsers()
    } catch (error: any) {
      showMessage?.(error?.message || t('invitation_email_failed'), 'error')
    }
  }

  const stateClass = (state: UserState) => state === 'ACTIVE'
    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
    : state === 'PENDING' ? 'border-amber-200 bg-amber-50 text-amber-700'
      : 'border-slate-200 bg-slate-100 text-slate-600'

  const stepNames = [
    'access.wizard.type', 'access.wizard.identity', 'access.wizard.scope',
    'access.wizard.role', 'access.wizard.platforms', 'access.wizard.overrides',
    'access.wizard.review'
  ]

  const wizardContent = () => {
    if (step === 1) return <div className="grid gap-3 md:grid-cols-2">
      <button onClick={() => setAccountType('CUSTOMER')} disabled={Boolean(selected?.isPlatformAdmin)}
        className={`rounded-xl border p-4 text-left ${draft.accountType === 'CUSTOMER' ? 'border-blue-400 bg-blue-50' : 'border-slate-200'}`}>
        <Building2 className="text-blue-600" /><div className="mt-3 font-semibold">{t('access.customerUsers')}</div>
        <p className="mt-1 text-xs text-slate-500">{t('access.wizard.customerTypeHint')}</p>
      </button>
      {isPlatformAdmin && <button onClick={() => setAccountType('FLEET_TEAM')} disabled={Boolean(selected?.isPlatformAdmin)}
        className={`rounded-xl border p-4 text-left ${draft.accountType === 'FLEET_TEAM' ? 'border-indigo-400 bg-indigo-50' : 'border-slate-200'}`}>
        <Users className="text-indigo-600" /><div className="mt-3 font-semibold">{t('access.fleetTeam')}</div>
        <p className="mt-1 text-xs text-slate-500">{t('access.wizard.fleetTypeHint')}</p>
      </button>}
      {isPlatformAdmin && <div className={`rounded-xl border p-4 md:col-span-2 ${selected?.isPlatformAdmin ? 'border-violet-300 bg-violet-50' : 'border-slate-200 bg-slate-50'}`}>
        <div className="flex items-center gap-2 font-semibold text-violet-800"><Shield size={18} />{t('access.platformAdmin')}</div>
        <p className="mt-1 text-xs text-slate-600">
          {selected?.isPlatformAdmin ? t('access.wizard.platformAdminCurrent') : t('access.wizard.platformAdminUnsupported')}
        </p>
      </div>}
    </div>

    if (step === 2) return <div className="grid gap-4 sm:grid-cols-2">
      {[
        ['username', t('username'), 'text'],
        ['fullName', t('full_name'), 'text'],
        ['email', t('email'), 'email']
      ].map(([key, label, type]) => <label key={key} className={`text-xs font-medium text-slate-600 ${key === 'email' ? 'sm:col-span-2' : ''}`}>
        {label}<input type={type} value={(draft as any)[key]}
          disabled={mode === 'edit' && !isPlatformAdmin}
          onChange={event => setDraft(current => ({ ...current, [key]: event.target.value }))}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-100" />
      </label>)}
      {mode === 'edit' && !isPlatformAdmin && <p className="text-xs text-slate-500 sm:col-span-2">{t('access.wizard.tenantIdentityLocked')}</p>}
    </div>

    if (step === 3) return <div className="space-y-4">
      {draft.accountType === 'FLEET_TEAM' && <div className="grid gap-3 sm:grid-cols-2">
        {(['GLOBAL', 'SELECTED_CUSTOMERS'] as DataScope[]).map(scope => <button key={scope}
          disabled={Boolean(selected?.isPlatformAdmin)}
          onClick={() => setDraft(current => ({ ...current, dataScope: scope, customerIds: scope === 'GLOBAL' ? [] : current.customerIds }))}
          className={`rounded-xl border p-4 text-left ${draft.dataScope === scope ? 'border-indigo-400 bg-indigo-50' : 'border-slate-200'}`}>
          {scope === 'GLOBAL' ? <Globe2 className="text-indigo-600" /> : <Building2 className="text-blue-600" />}
          <div className="mt-2 font-medium">{t(scope === 'GLOBAL' ? 'access.globalScope' : 'access.selectedScope')}</div>
          <p className="mt-1 text-xs text-slate-500">{t(scope === 'GLOBAL' ? 'access.wizard.globalHint' : 'access.wizard.selectedHint')}</p>
        </button>)}
      </div>}
      {(draft.accountType === 'CUSTOMER' || draft.dataScope === 'SELECTED_CUSTOMERS') && <section>
        <h4 className="mb-2 text-sm font-semibold text-slate-800">{t('customer_workspaces')}</h4>
        <div className="max-h-64 space-y-1 overflow-y-auto rounded-xl border border-slate-200 p-2">
          {customers.map(customer => <label key={customer.id} className="flex items-center gap-2 rounded-lg p-2 text-sm hover:bg-slate-50">
            <input type="checkbox" checked={draft.customerIds.includes(customer.id)} disabled={!isPlatformAdmin}
              onChange={() => toggleCustomer(customer.id)} className="rounded border-slate-300 text-blue-600" />
            <span>{customer.companyName}</span>
            {customer.customerCode && <span className="text-xs text-slate-400">({customer.customerCode})</span>}
            {!isPlatformAdmin && <span className="ml-auto text-[10px] text-slate-400">{t('access.locked')}</span>}
          </label>)}
        </div>
      </section>}
    </div>

    if (step === 4) {
      if (selected?.isPlatformAdmin) return <div className="rounded-xl border border-violet-200 bg-violet-50 p-5">
        <div className="flex items-center gap-2 font-semibold text-violet-800"><Shield size={18} />{t('access.platformAdmin')}</div>
        <p className="mt-2 text-sm text-violet-700">{t('access.wizard.platformAdminRoleHint')}</p>
      </div>
      const rows = draft.accountType === 'FLEET_TEAM'
        ? [{ scope: FLEET_SCOPE, label: t('access.fleetTeam'), roleId: draft.fleetRoleTemplateId, choices: fleetRoles }]
        : draft.customerIds.map(customerId => ({
          scope: String(customerId),
          label: customers.find(customer => customer.id === customerId)?.companyName || String(customerId),
          roleId: draft.roleByCustomer[String(customerId)] || null,
          choices: customerRoles
        }))
      return <div className="space-y-3">{rows.map(row => {
        const selectedRole = roles.find(role => role.id === row.roleId)
        return <section key={row.scope} className="rounded-xl border border-slate-200 p-4">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><Building2 size={15} />{row.label}</div>
          <select value={row.roleId || ''} onChange={event => chooseRole(row.scope, Number(event.target.value))}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm">
            <option value="">{t('access.wizard.chooseRole')}</option>
            {row.choices.map(role => <option key={role.id} value={role.id}>{role.displayName}</option>)}
          </select>
          {selectedRole && <div className="mt-3 rounded-lg bg-slate-50 p-3">
            <p className="text-xs text-slate-600">{selectedRole.description || t('access.roles.noDescription')}</p>
            <div className="mt-2 flex flex-wrap gap-1">{baseCapabilities(row.scope).map(key =>
              <span key={key} className="rounded bg-white px-2 py-1 text-[10px] text-slate-600">{key}</span>)}</div>
          </div>}
        </section>
      })}</div>
    }

    if (step === 5) {
      const requiredPlatforms = requiredPlatformsFor(draft.accountType, draft.customerIds)
      return <div className="space-y-3">
      {platforms.map(platform => {
        const required = requiredPlatforms.includes(platform.key)
        const checked = required || draft.platforms.includes(platform.key)
        return <label key={platform.key} className={`flex items-center gap-3 rounded-xl border p-4 ${
          required ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200'
        }`}>
          <input type="checkbox" checked={checked} disabled={required || !isPlatformAdmin}
            onChange={() => setDraft(current => ({
              ...current,
              platforms: current.platforms.includes(platform.key)
                ? current.platforms.filter(item => item !== platform.key)
                : [...current.platforms, platform.key]
            }))} className="rounded border-slate-300 text-indigo-600" />
          <span><span className={`block text-sm font-semibold ${required ? 'text-emerald-800' : 'text-slate-800'}`}>{platform.name}</span>
            {required && <span className="text-xs text-emerald-700">
              {t('access.wizard.requiredPlatformHint', { defaultValue: 'Required for the selected customer.' })}
            </span>}</span>
        </label>
      })}
      {!isPlatformAdmin && <p className="text-xs text-slate-500">{t('access.wizard.otherPlatformsAdminOnly')}</p>}
    </div>
    }

    if (step === 6) {
      if (selected?.isPlatformAdmin) return <div className="rounded-xl border border-violet-200 bg-violet-50 p-5">
        <div className="flex items-center gap-2 font-semibold text-violet-800"><Shield size={18} />{t('access.platformAdmin')}</div>
        <p className="mt-2 text-sm text-violet-700">{t('access.wizard.platformAdminRoleHint')}</p>
      </div>
      const scopes = draft.accountType === 'FLEET_TEAM'
        ? [{ key: FLEET_SCOPE, label: t('access.fleetTeam') }]
        : draft.customerIds.map(id => ({ key: String(id), label: customers.find(customer => customer.id === id)?.companyName || String(id) }))
      const base = baseCapabilities(draft.capabilityScope)
      const effective = effectiveCapabilities(draft.capabilityScope)
      return <div className="space-y-4">
        <label className="flex items-start gap-3 rounded-xl border border-slate-200 p-4">
          <input type="checkbox" checked={draft.advanced}
            onChange={event => setDraft(current => ({ ...current, advanced: event.target.checked }))}
            className="mt-0.5 rounded border-slate-300 text-indigo-600" />
          <span><span className="block text-sm font-semibold">{t('access.wizard.enableOverrides')}</span>
            <span className="text-xs text-slate-500">{t('access.wizard.overrideHint')}</span></span>
        </label>
        {draft.advanced && <>
          {scopes.length > 1 && <select value={draft.capabilityScope}
            onChange={event => setDraft(current => ({ ...current, capabilityScope: event.target.value }))}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm">
            {scopes.map(scope => <option key={scope.key} value={scope.key}>{scope.label}</option>)}
          </select>}
          <div className="max-h-[42vh] space-y-3 overflow-y-auto">
            {Object.entries(groupedCapabilities).map(([category, items]) => <section key={category} className="rounded-xl border border-slate-200 p-3">
              <div className="mb-2 text-[10px] font-bold uppercase tracking-wide text-slate-400">{category}</div>
              <div className="grid gap-1 sm:grid-cols-2">{items.map(capability => {
                const granted = effective.includes(capability.key)
                const changed = granted !== base.includes(capability.key)
                return <label key={capability.key} className={`flex items-start gap-2 rounded-lg p-2 text-xs ${
                  dangerousCapabilities.has(capability.key) ? 'bg-amber-50' : 'bg-slate-50'
                }`}>
                  <input type="checkbox" checked={granted} onChange={() => toggleCapability(capability.key)}
                    className="mt-0.5 rounded border-slate-300 text-indigo-600" />
                  <span><span className="font-medium">{capability.key}</span>
                    {changed && <span className={`ml-1 rounded px-1.5 py-0.5 text-[9px] ${granted ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                      {t(granted ? 'access.wizard.added' : 'access.wizard.removed')}
                    </span>}
                    {capability.description && <span className="mt-0.5 block text-[10px] text-slate-500">{capability.description}</span>}</span>
                </label>
              })}</div>
            </section>)}
          </div>
          {effective.some(key => dangerousCapabilities.has(key)) && <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            <AlertTriangle size={15} />{t('access.roles.dangerousWarning')}
          </div>}
        </>}
      </div>
    }

    const roleSummary = draft.accountType === 'FLEET_TEAM'
      ? [roles.find(role => role.id === draft.fleetRoleTemplateId)?.displayName]
      : draft.customerIds.map(id => `${customers.find(customer => customer.id === id)?.companyName || id}: ${roles.find(role => role.id === draft.roleByCustomer[String(id)])?.displayName || '—'}`)
    return <div className="space-y-3">
      <div className="rounded-xl border border-slate-200 p-4">
        <div className="flex items-center gap-2 font-semibold"><User size={17} />{draft.fullName}</div>
        <div className="mt-1 text-xs text-slate-500">{draft.username} · {draft.email}</div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-xl bg-slate-50 p-4"><div className="text-[10px] font-bold uppercase text-slate-400">{t('access.wizard.type')}</div>
          <div className="mt-1 text-sm font-medium">{t(draft.accountType === 'CUSTOMER' ? 'access.customerUsers' : 'access.fleetTeam')}</div></div>
        <div className="rounded-xl bg-slate-50 p-4"><div className="text-[10px] font-bold uppercase text-slate-400">{t('access.wizard.scope')}</div>
          <div className="mt-1 text-sm font-medium">{draft.dataScope === 'GLOBAL' ? t('access.globalScope') : t('access.selectedScope')}</div></div>
      </div>
      <div className="rounded-xl bg-slate-50 p-4"><div className="text-[10px] font-bold uppercase text-slate-400">{t('access.wizard.role')}</div>
        <div className="mt-2 space-y-1 text-sm">{roleSummary.map((item, index) => <div key={index}>{item}</div>)}</div></div>
      <div className="rounded-xl bg-slate-50 p-4"><div className="text-[10px] font-bold uppercase text-slate-400">{t('platform_access')}</div>
        <div className="mt-2 flex flex-wrap gap-1">{[...new Set([
          ...requiredPlatformsFor(draft.accountType, draft.customerIds),
          ...draft.platforms
        ])].map(item => <span key={item} className="rounded bg-white px-2 py-1 text-xs">{item}</span>)}</div></div>
      {mode === 'edit' && !['PENDING', 'REVOKED'].includes(draft.state) && <label className="block rounded-xl border border-slate-200 p-4 text-xs font-medium text-slate-600">
        {t('status')}
        <select value={draft.state} onChange={event => setDraft(current => ({ ...current, state: event.target.value as UserState }))}
          className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm">
          <option value="ACTIVE">{t('active')}</option>
          <option value="SUSPENDED">{t('inactive')}</option>
        </select>
      </label>}
    </div>
  }

  return (
    <div ref={containerRef} className="relative flex h-full min-h-0 flex-col overflow-hidden bg-white">
      <div className="border-b border-slate-200 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex gap-2"><Users className="text-blue-600" /><div>
            <h2 className="font-semibold">{t('user_management')}</h2>
            <p className="text-xs text-slate-500">{t('access.users.portalOnlyHint')}</p>
          </div></div>
          <button onClick={startCreate} disabled={!can('users.manage')}
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm text-white disabled:opacity-50">
            <Plus size={15} />{t('invite_user')}
          </button>
        </div>
        <div className="mt-4 flex gap-1 overflow-x-auto rounded-xl bg-slate-100 p-1">
          {([
            ['CUSTOMER', Building2, 'access.customerUsers'],
            ['FLEET_TEAM', Users, 'access.fleetTeam'],
            ['INVITATIONS', Mail, 'access.invitations.title']
          ] as const).map(([key, Icon, label]) => {
            if (key === 'FLEET_TEAM' && !isPlatformAdmin) return null
            const count = users.filter(user => key === 'INVITATIONS' ? user.state === 'PENDING' : user.state !== 'PENDING' && user.accountType === key).length
            return <button key={key} onClick={() => setView(key)}
              className={`flex min-w-max flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-medium ${
                view === key ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-600'
              }`}><Icon size={14} />{t(label)}<span className="rounded-full bg-slate-100 px-1.5 text-[10px]">{count}</span></button>
          })}
        </div>
        <div className="mt-3 flex gap-2">
          <div className="relative flex-1"><Search className="absolute left-3 top-2.5 text-slate-400" size={15} />
            <input value={search} onChange={event => setSearch(event.target.value)} placeholder={t('search_users')}
              className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm" /></div>
          <button onClick={() => setShowFilters(value => !value)} className="rounded-lg border border-slate-300 px-3 text-slate-600"><Filter size={15} /></button>
        </div>
        {showFilters && <div className="mt-2 grid gap-2 rounded-lg bg-slate-50 p-2 sm:grid-cols-2">
          <select value={stateFilter} onChange={event => setStateFilter(event.target.value)} className="rounded border border-slate-300 p-2 text-xs">
            <option value="current">{t('current_users', { defaultValue: 'Current users' })}</option><option value="all">{t('all_users')}</option><option value="ACTIVE">ACTIVE</option><option value="SUSPENDED">SUSPENDED</option><option value="REVOKED">REVOKED</option><option value="PENDING">PENDING</option>
          </select>
          <select value={scopeFilter} onChange={event => setScopeFilter(event.target.value)} className="rounded border border-slate-300 p-2 text-xs">
            <option value="all">{t('access.allScopes')}</option><option value="GLOBAL">{t('access.globalScope')}</option><option value="SELECTED_CUSTOMERS">{t('access.selectedScope')}</option>
          </select>
        </div>}
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        {loading ? <div className="p-8 text-center text-sm text-slate-400">{t('loading_users')}</div>
          : !filteredUsers.length ? <div className="p-8 text-center text-sm text-slate-400">{t('no_users_found')}</div>
            : <div className="space-y-3">{filteredUsers.map(user => <article key={user.id} className="rounded-xl border border-slate-200 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="font-semibold text-slate-900">{user.fullName}</span>
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] ${stateClass(user.state)}`}>{user.state}</span>
                    <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] text-blue-700">{t(user.accountType === 'CUSTOMER' ? 'access.customerAccount' : 'access.fleetAccount')}</span>
                    {user.isPlatformAdmin && <span className="flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] text-violet-700"><Shield size={10} />{t('access.platformAdmin')}</span>}
                    {user.dataScope === 'GLOBAL' && <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] text-indigo-700">{t('access.globalScope')}</span>}
                    {user.workspaces.length > 1 && <span className="rounded-full bg-cyan-100 px-2 py-0.5 text-[10px] text-cyan-700">{t('access.multiWorkspace', { count: user.workspaces.length })}</span>}
                  </div>
                  <div className="mt-1 text-xs text-slate-500">{user.username} · {user.email}</div>
                  <div className="mt-3 space-y-1">
                    {(user.accountType === 'CUSTOMER' ? user.memberships : user.workspaces).map(membership =>
                      <div key={membership.customerId} className="flex flex-wrap items-center gap-1.5 rounded-lg bg-slate-50 px-2 py-1.5 text-[10px] text-slate-600">
                        <Building2 size={11} /><span className="font-medium">{membership.customerName || membership.customerId}</span>
                        {user.accountType === 'CUSTOMER' && <span className="rounded bg-white px-1.5 py-0.5">{membership.roleTemplate?.displayName || membership.role}</span>}
                        {membership.platforms.map(platform => <span key={platform} className="rounded bg-white px-1.5 py-0.5">{platform}</span>)}
                      </div>)}
                    {user.accountType === 'FLEET_TEAM' && <div className="text-[10px] text-slate-500">
                      {t('access.roleTemplate')}: {user.roleTemplate?.displayName || roles.find(role => role.id === user.roleTemplateId)?.displayName || '—'} · {user.platforms.join(', ') || FLEET_PLATFORM}
                    </div>}
                  </div>
                </div>
                <div className="flex shrink-0 gap-1">
                  {user.state === 'PENDING' && <>
                    <button onClick={() => invitationAction(user, 'resend')} title={t('access.invitations.resend')} className="p-2 text-slate-400 hover:text-emerald-600"><RefreshCw size={14} /></button>
                    <button onClick={() => invitationAction(user, 'revoke')} title={t('access.invitations.revoke')} className="p-2 text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                  </>}
                  <button onClick={() => startEdit(user)} disabled={user.state === 'REVOKED'} className="p-2 text-slate-400 hover:text-blue-600 disabled:opacity-25"><Edit3 size={14} /></button>
                  {user.state !== 'PENDING' && <button onClick={() => revokeAccess(user)} disabled={user.state === 'REVOKED'} className="p-2 text-slate-400 hover:text-red-600 disabled:opacity-25"><Trash2 size={14} /></button>}
                </div>
              </div>
            </article>)}</div>}
      </div>

      {mode && <div className="fixed inset-0 z-[300] flex items-center justify-center bg-slate-950/50 p-2 sm:p-4" onMouseDown={closeWizard}>
        <div className="flex max-h-[94vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl" onMouseDown={event => event.stopPropagation()}>
          <header className="border-b border-slate-200 p-4">
            <div className="flex items-start justify-between gap-3">
              <div><h3 className="font-semibold">{mode === 'create' ? t('invite_user') : t('edit_user')}</h3>
                <p className="text-xs text-slate-500">{t('access.wizard.step', { current: step, total: 7 })} · {t(stepNames[step - 1])}</p></div>
              <button onClick={closeWizard} className="p-1 text-slate-400"><X size={18} /></button>
            </div>
            <div className="mt-4 flex gap-1">{stepNames.map((name, index) =>
              <button key={name} onClick={() => index + 1 < step && setStep(index + 1)}
                className={`h-1.5 flex-1 rounded-full ${index + 1 <= step ? 'bg-blue-600' : 'bg-slate-200'}`}
                title={t(name)} />)}</div>
          </header>
          <main className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
            <div className="mx-auto max-w-3xl">
              <h4 className="mb-4 text-lg font-semibold text-slate-900">{t(stepNames[step - 1])}</h4>
              {wizardContent()}
            </div>
          </main>
          <footer className="flex items-center justify-between gap-2 border-t border-slate-200 p-4">
            <button onClick={() => step === 1 ? closeWizard() : setStep(current => current - 1)}
              className="flex items-center gap-1 rounded-lg bg-slate-100 px-4 py-2 text-sm text-slate-700">
              {step > 1 && <ChevronLeft size={15} />}{step === 1 ? t('cancel') : t('back')}
            </button>
            {step < 7 ? <button onClick={next} className="flex items-center gap-1 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white">
              {t('continue')}<ChevronRight size={15} />
            </button> : <button onClick={save} disabled={saving || !can('users.manage')}
              className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
              {saving ? '…' : <><Check size={15} />{mode === 'create' ? t('send_invitation') : t('update')}</>}
            </button>}
          </footer>
        </div>
      </div>}
    </div>
  )
}

export default UserManagement
